import { useState } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@/shared/lib/utils/cn';
import { Avatar } from '@/shared/ui';
import { formatDate } from '@/shared/lib/utils/date';
import { useAuth, useAuthModal } from '@/features/auth';
import { useToggleCommentLike } from '../api/useComments';
import { CommentComposer } from './CommentComposer';
import type { CommentNode } from '../model/types';

export function CommentItem({ articleId, comment }: { articleId: string; comment: CommentNode }) {
  const { user } = useAuth();
  const openAuth = useAuthModal((state) => state.open);
  const like = useToggleCommentLike(articleId);
  const [replying, setReplying] = useState(false);

  const requireAuth = (fn: () => void) => () => {
    if (!user) {
      openAuth('login');
      return;
    }
    fn();
  };

  return (
    <div className="flex gap-3">
      <Avatar
        initials={comment.author?.initials ?? '?'}
        color={comment.author?.avatarColor ?? 'bg-zinc-500'}
        src={comment.author?.avatarUrl}
        alt={comment.author ? `${comment.author.name}'s profile picture` : undefined}
        size="sm"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-foreground">
            {comment.author?.name ?? 'Someone'}
          </span>
          <span className="font-mono text-[10px] text-muted-foreground">
            {formatDate(comment.createdAt)}
          </span>
        </div>
        <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground">
          {comment.body}
        </p>

        <div className="mt-2 flex items-center gap-4">
          <button
            type="button"
            aria-pressed={comment.likedByMe}
            aria-label={comment.likedByMe ? 'Unlike comment' : 'Like comment'}
            onClick={requireAuth(() => like.mutate({ commentId: comment.id, liked: comment.likedByMe }))}
            className={cn(
              'flex items-center gap-1.5 text-xs transition-colors',
              comment.likedByMe
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Heart className={cn('h-3.5 w-3.5', comment.likedByMe && 'fill-current')} />
            {comment.likeCount > 0 ? comment.likeCount : 'Like'}
          </button>
          <button
            type="button"
            onClick={requireAuth(() => setReplying((value) => !value))}
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Reply
          </button>
        </div>

        {replying ? (
          <div className="mt-3">
            <CommentComposer
              articleId={articleId}
              parentId={comment.id}
              placeholder="Write a reply…"
              onDone={() => setReplying(false)}
            />
          </div>
        ) : null}

        {comment.replies.length > 0 ? (
          <div className="mt-4 space-y-4 border-l-2 border-border pl-4">
            {comment.replies.map((reply) => (
              <CommentItem key={reply.id} articleId={articleId} comment={reply} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
